import { isValidElement, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { useIsMobile } from '@/lib/utils/useIsMobile';

/**
 * 모달 껍데기.
 *
 * v1 은 `.modal-overlay / .modal-content / .modal-header …` 마크업을 컴포넌트마다 손으로 반복했다.
 * 그 결과 Esc 키 처리가 어떤 모달에는 있고 어떤 모달에는 없다.
 * 여기 한 곳에 모아 두면 이식되는 화면들이 자동으로 같은 동작을 갖는다.
 *
 * 클래스 이름은 v1 `globals.css` 의 것을 그대로 쓴다 — 스타일은 이식된 CSS 가 이미 갖고 있다.
 *
 * <h2>배경을 눌러도 닫히지 않는다</h2>
 * 모달은 대부분 <b>입력 중인 판</b>이다. 표를 옮기려 드래그가 살짝 빗나가거나 넓은 여백을 무심히
 * 누른 것만으로 적던 내용이 통째로 사라지면, 그 손실은 되돌릴 방법이 없고 사라졌다는 사실조차
 * 늦게 안다. 닫기는 <b>닫겠다고 말한 경우</b>에만 — X 버튼·직접 만든 취소 버튼·Esc — 일어난다.
 *
 * <h2>Esc 는 맨 위 하나만 닫는다</h2>
 * 모달 위에 모달이 열리는 자리가 있다(직원찾기 → 본사 평면도). Esc 를 `window` 에서 듣는 구조라
 * 그대로 두면 <b>열려 있는 모달이 전부 함께 닫힌다</b> — 도면을 닫으려던 사람이 직원찾기까지 잃는다.
 * 그래서 열린 순서를 스택으로 들고, 가장 나중에 열린 것만 Esc 에 반응한다.
 *
 * <h2>어디서 열리든 «몸통» 에 붙는다 (사용자 지시 2026-08-26)</h2>
 * 모달 안에서 연 모달은 그 자리에 그려지면 <b>부모의 상자에 갇힌다</b> — 부모 본문의
 * {@code overflow: auto} 가 고정 위치의 덮개를 거기서 잘라 낸다. 그래서 어두운 덮개가 화면
 * 높이를 못 채우고 부모 창 크기만큼만 덮였다.
 *
 * <p>{@code createPortal} 로 언제나 {@code document.body} 에 붙인다. 화면 어디에서 열었든 덮개는
 * 창 전체이고, 나중에 연 모달이 항상 위에 온다(붙는 순서 = 쌓이는 순서).
 *
 * <p>덮개가 <b>상단바까지</b> 덮는 것은 `.modal-overlay` 의 쌓임 순서와 「흐림 없음」 이 맡는다
 * (index.css 머리말) — 그쪽을 고치면 이 창들이 전부 함께 고쳐진다.
 */

/** 열려 있는 모달들(열린 순서). 마운트 순서가 곧 쌓인 순서다 — 자식 모달이 항상 뒤에 붙는다. */
const openModals: symbol[] = [];
export function Modal({
  title,
  actions,
  onClose,
  children,
  footer,
  maxWidth = '640px',
  closeDisabled = false,
  beforeClose,
  tourKey,
  titleWrap = false,
}: {
  title: string;
  /**
   * 제목 줄 <b>오른쪽 끝</b>에 붙는 것. 이 모달로 무엇을 할 수 있는지가 제목과 한 줄에서 읽혀야
   * 하는 경우에 쓴다(자리배치도의 「수정하기」·「내려받기」). 닫기 버튼보다 앞에 선다.
   */
  actions?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  maxWidth?: string;
  /** 전송 중처럼 닫으면 곤란한 순간에 Esc·X 를 모두 막는다. */
  closeDisabled?: boolean;
  /**
   * 닫기 직전에 묻는다. {@code false} 를 주면 <b>닫지 않는다</b> — Esc·X 둘 다 같은 길로
   * 지나가므로 한 곳만 막으면 된다.
   *
   * <p>{@link closeDisabled} 와 다르다: 그쪽은 "지금은 못 닫는다"(전송 중)이고, 이쪽은 "닫기 전에
   * 물어볼 것이 있다"(저장하지 않은 변경)다. 막는 대신 <b>스스로 물음을 띄우는</b> 쪽이 부른다.
   */
  beforeClose?: () => boolean;
  /**
   * 안내 투어가 이 창을 짚을 때 쓰는 이름(`data-tour`). 모달은 <b>포털로 그려지므로</b> 부르는 쪽에서
   * 감싸는 것으로는 잡을 수 없다 — 이름표를 여기까지 내려 준다.
   */
  tourKey?: string;
  /**
   * 제목을 <b>여러 줄로 접는다</b> (사용자 지시 2026-09-11).
   *
   * <p>기본은 한 줄이다. 그래야 창을 열 때마다 머리 줄 높이가 같고, 버튼이 제목에 밀려 두 줄로
   * 갈라지지 않는다. 그런데 제목이 <b>값 셋을 잇는</b> 창이 있다 — 「완주 현장 · 강관비계
   * Ø48.6×2.4t」처럼. 그런 제목은 안 접으면 버튼을 창 밖으로 밀어내고, 사람은 가로로 끌어야
   * 버튼을 본다.
   *
   * <p>버튼을 아래로 내리지 <b>않는다</b> — 이 앱의 모달 버튼은 제목 줄이 규칙이다. 접히는 것은
   * 제목뿐이고, 그래서 이 스위치는 <b>켜는 창만</b> 켠다.
   */
  titleWrap?: boolean;
}) {
  const idRef = useRef<symbol | null>(null);
  idRef.current ??= Symbol('modal');

  /*
    버튼 자리를 가르는 유일한 값. 767px 이하만 «모바일» 이라, <b>아이패드 세로(768)와 노트북은
    웹과 같이 제목 줄</b>이다(사용자 지시 2026-08-31). 사이드바가 서랍으로 물러나는 지점(lg·1024)
    과는 <b>다른 선</b>이다 — 여기서 묻는 것은 「사이드바가 자리를 차지하는가」 가 아니라
    「한 줄에 제목과 버튼이 함께 들어가는가」 이고, 그것은 768 부터 된다.
  */
  const isMobile = useIsMobile();

  useEffect(() => {
    const id = idRef.current!;
    openModals.push(id);
    return () => {
      const at = openModals.lastIndexOf(id);
      if (at >= 0) openModals.splice(at, 1);
    };
  }, []);

  useEffect(() => {
    if (closeDisabled) return;

    const handleEscape = (event: KeyboardEvent) => {
      // 위에 다른 모달이 떠 있으면 이 모달의 차례가 아니다.
      if (event.key !== 'Escape' || openModals[openModals.length - 1] !== idRef.current) return;
      if (beforeClose && !beforeClose()) return;
      onClose();
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [closeDisabled, onClose, beforeClose]);

  const requestClose = () => {
    if (closeDisabled) return;
    if (beforeClose && !beforeClose()) return;
    onClose();
  };

  return createPortal(
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-label={title}>
      <div className="modal-content" style={{ maxWidth }} data-tour={tourKey}>
        <div className={titleWrap ? 'modal-header modal-title-wrap' : 'modal-header'}>
          <h2 className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>
            {title}
          </h2>
          {/*
            제목 줄의 버튼들 — <b>웹에서만</b>이다. 모바일은 아래 바닥으로 내려간다(사용자 지시
            2026-08-31). 자세한 사정은 아래 바닥 쪽 주석에 적었다.

            <p>줄이 모자라면 <b>옆으로 민다</b>(가로 스크롤). 접어서 두 줄로 만들지 않는다 —
            머리 줄이 두 줄이 되면 그만큼 본문이 줄고, 창을 열 때마다 높이가 달라 보인다.
            자식이 눌려 찌그러지지 않게 하는 `flex-shrink: 0` 은 `index.css` 의
            `.modal-header-actions` 가 준다.
          */}
          {!isMobile && (actions || footer) && (
            <div
              className="modal-header-actions"
              style={{
                display: 'flex',
                gap: '6px',
                alignItems: 'center',
                marginLeft: 'auto',
                marginRight: '8px',
                minWidth: 0,
                overflowX: 'auto',
              }}
            >
              {actions}
              {/*
                ★ {@code footer} 로 받은 것도 <b>웹에서는 여기로 올라온다</b>(사용자 지시 2026-08-31).

                이 앱의 모달 버튼은 제목 줄이 규칙인데, 화면이 75곳이라 옮기다 만 것들이 남아 있었다.
                부르는 쪽을 하나씩 고치면 또 빠뜨리므로 <b>자리를 정하는 일을 여기로 모은다</b> —
                `footer` 로 주든 `actions` 로 주든 결과가 같아지고, 앞으로 새로 만드는 모달도 저절로
                규칙을 따른다.
              */}
              {footer}
            </div>
          )}
          <button onClick={requestClose} className="btn-ghost p-1" disabled={closeDisabled} aria-label="닫기">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="modal-body space-y-3">{children}</div>

        {/*
          <b>모바일에서만</b> 제목 줄의 버튼이 여기로 내려온다 (사용자 지시 2026-08-31).

          <p>웹에서는 제목 줄이 맞다 — 창이 길어도 저장하러 끝까지 내려가지 않는다. 그런데 좁은
          화면에서는 제목 + 버튼 넷 + 닫기가 <b>한 줄에 물리적으로 안 들어간다.</b> 이름을 줄이고
          가로로 밀어 봐도, 폭이 360px 이면 결국 버튼이 화면 밖에 숨는다.

          <p>바닥으로 내리면서 <b>바닥을 창에 붙인다</b>(`index.css` 의 `.modal-footer` 모바일
          규칙 — `position: sticky`). 제목 줄에 둔 이유가 「끝까지 안 내려가게」 였으므로, 자리를
          옮기면서 그 이유를 버리면 안 된다.

          <p>DOM 을 두 벌 만들지 않고 <b>둘 중 하나만</b> 그린다(`useIsMobile`). 두 벌이면 같은
          `data-tour` 이름표가 둘이 되어 안내 투어가 화면에 없는 쪽을 가리킨다.
        */}
        {isMobile && (actions || footer) && (
          <div className="modal-footer">
            {actions}
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

/** 모달·폼에서 반복되는 라벨 + 입력 묶음. */
/** @param label 글자만이 아니라 <b>토막</b>도 받는다 — 필수 표시(붉은 *)를 붙이는 자리가 있다. */
export function Field({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div>
      <label
        style={{
          display: 'block',
          fontSize: '12px',
          fontWeight: 600,
          color: 'var(--text-secondary)',
          marginBottom: '6px',
        }}
      >
        {label}
      </label>
      {children}
    </div>
  );
}

/**
 * 성공·실패 메시지 띠. v1 이 화면마다 인라인 스타일로 복사해 두던 것.
 *
 * <h2>주황 경고는 <b>모두</b> 닫을 수 있다 (사용자 지시 2026-09-07)</h2>
 * 화면 위에 계속 서 있는 주황 띠 — 「확인 필요 n줄」·「도착 미확인 n건」 — 는 그 일을 지금 할
 * 생각이 없는 사람에게 지워지지 않는 잔소리다. 그렇다고 화면마다 닫는 스위치를 따로 달면 어떤
 * 띠는 닫히고 어떤 띠는 안 닫히는 화면이 된다. 그래서 <b>이 컴포넌트가</b> 한 곳에서 세운다:
 * {@code tone="warning"} 이면 오른쪽 끝에 ✕ 가 서고, 부르는 쪽은 아무것도 안 해도 된다
 * (백 곳 넘는 자리가 그대로 따라온다).
 *
 * <p>파랑·초록·빨강은 그대로 둔다. 알림(info)·성공은 곧 사라지는 말이고, <b>빨강은 실패</b>라
 * 닫는 것으로 없앨 일이 아니다.
 *
 * <h2>닫은 것은 «그 문장»이다 — 다음 경고는 다시 뜬다</h2>
 * 접힘을 그냥 켜고 끄면, 창 안에서 문구만 바뀌는 경고(입력 검증 · 업로드 실패)가 한 번 닫힌 뒤
 * <b>영영 안 뜬다</b>. 무엇을 닫았는지 문장으로 기억해, 내용이 바뀌면 다시 세운다.
 *
 * @param closable 이 띠만 ✕ 를 끄고 싶을 때 {@code false}. 기본은 경고면 켜짐이다
 * @param onClose  닫을 때 부르는 쪽도 알아야 하면 준다(대개 필요 없다 — 띠는 스스로 접힌다)
 */
export function Notice({
  tone = 'info',
  closable,
  onClose,
  children,
}: {
  tone?: 'info' | 'success' | 'error' | 'warning';
  closable?: boolean;
  onClose?: () => void;
  children: ReactNode;
}) {
  const palette = {
    info: { background: '#eff6ff', color: '#1d4ed8' },
    success: { background: '#dcfce7', color: '#15803d' },
    error: { background: '#fee2e2', color: '#b91c1c' },
    warning: { background: '#fef3c7', color: '#92400e' },
  }[tone];

  const showClose = closable ?? tone === 'warning';
  const [closedText, setClosedText] = useState<string | null>(null);
  const text = textOf(children);

  if (showClose && closedText !== null && closedText === text) return null;

  return (
    <div style={{ padding: '10px 12px', borderRadius: '10px', fontSize: '12px', fontWeight: 600, ...palette }}>
      {showClose ? (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
          <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
          <button
            type="button"
            className="no-print"
            onClick={() => {
              setClosedText(text);
              onClose?.();
            }}
            aria-label="닫기"
            title="닫기"
            style={{
              background: 'transparent',
              border: 'none',
              // 글자와 <b>같은 색</b>이라야 띠 안의 것으로 읽힌다. 회색으로 두면 남의 것처럼 뜬다.
              color: 'inherit',
              cursor: 'pointer',
              padding: '0 2px',
              lineHeight: 1,
              flexShrink: 0,
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
      ) : (
        children
      )}
    </div>
  );
}

/**
 * 띠에 적힌 <b>글자만</b> 훑어 모은다 — 무엇을 닫았는지 기억하는 열쇠다.
 *
 * <p>버튼·굵은 글씨가 섞인 띠라도 사람이 읽는 문장은 하나다. 요소를 그대로 비교하면 매번
 * 새 객체라 늘 「다른 경고」가 되고, 그러면 ✕ 를 눌러도 곧바로 되살아난다.
 */
function textOf(node: ReactNode): string {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(textOf).join('');
  if (isValidElement<{ children?: ReactNode }>(node)) return textOf(node.props.children);
  return '';
}
